import React, { useState, useEffect, useRef } from 'react';
import autoAnimate from '@formkit/auto-animate';
import { useQAndAInfinite, useCreateQuestionMutation, useVoteQuestionMutation, useUnvoteQuestionMutation } from '../api';
import { getAnonymousUserId } from '../utils/anonymousUserId';
import { getUserName, saveUserNameFromModal, markNameModalSkipped } from '../utils/userName';
import { Button, PreviewNotice } from './ui';
import { PiThumbsUp, PiThumbsUpFill, PiPencilSimpleLine } from "react-icons/pi";
import NameModal from './NameModal';
import { useGallery } from '../context/GalleryContext';
import { PREVIEW_ONLY_MESSAGE, isPreviewReadOnlyError } from '../utils/previewReadOnly';

const QAndA = () => {
  const { isPreview } = useGallery();
  const [name, setName] = useState('');
  const [question, setQuestion] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [newQuestionId, setNewQuestionId] = useState(null);
  const [votedQuestionIds, setVotedQuestionIds] = useState(new Set());
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [pendingName, setPendingName] = useState('');

  const anonymousUserId = getAnonymousUserId();
  const questionRefs = useRef({});
  const listContainerRef = useRef(null);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useQAndAInfinite(50, anonymousUserId, {
    refetchInterval: 10000, // Poll every 10 seconds for live updates across devices
  });

  const createQuestionMutation = useCreateQuestionMutation();
  const voteQuestionMutation = useVoteQuestionMutation();
  const unvoteQuestionMutation = useUnvoteQuestionMutation();

  // Flatten all pages of questions
  const allQuestions = data?.pages?.flatMap(page => page.items) || [];

  // Setup autoAnimate using callback ref for reliable initialization
  const setupAutoAnimate = useRef(null);
  const animateRef = (node) => {
    // Store the node reference for other uses
    listContainerRef.current = node;
    
    // Only setup animation once per node
    if (node && setupAutoAnimate.current !== node) {
      setupAutoAnimate.current = node;
      autoAnimate(node, { duration: 400 });
    }
  };

  // Load name from localStorage on mount
  useEffect(() => {
    const savedName = getUserName();
    if (savedName) {
      setName(savedName);
      return;
    }
  }, []);

  // Track which questions user has voted on
  useEffect(() => {
    if (data?.pages) {
      const votedIds = new Set();
      data.pages.forEach(page => {
        page.items.forEach(item => {
          if (item.hasVoted) {
            votedIds.add(item.id);
          }
        });
      });
      setVotedQuestionIds(votedIds);
    }
  }, [data]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Portal preview is read-only: the API rejects guest writes from a preview token.
    if (isPreview) {
      return;
    }

    if (!name.trim() || !question.trim()) {
      setSubmitMessage('Please fill in both name and question fields.');
      setTimeout(() => setSubmitMessage(''), 3000);
      return;
    }

    if (name.length > 200 || question.length > 2000) {
      setSubmitMessage('Name or question is too long. Please shorten your question.');
      setTimeout(() => setSubmitMessage(''), 3000);
      return;
    }

    setSubmitMessage('');

    try {
      const trimmedName = name.trim();
      const result = await createQuestionMutation.mutateAsync({ 
        name: trimmedName, 
        question: question.trim() 
      });
      // Save the trimmed name to localStorage
      saveUserNameFromModal(trimmedName);
      setName(trimmedName);
      setQuestion('');
      setTimeout(() => setSubmitMessage(''), 3000);
      
      // Track the new question ID for animation and scrolling
      if (result && result.id) {
        setNewQuestionId(result.id);
        
        // Wait for the DOM to update and data to refetch, then scroll to the new question
        // Use multiple timeouts to ensure the element is rendered
        const scrollToQuestion = () => {
          const questionElement = questionRefs.current[result.id];
          if (questionElement) {
            questionElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
            return true;
          }
          return false;
        };
        
        // Try scrolling after 100ms
        setTimeout(() => {
          if (!scrollToQuestion()) {
            // If not found, try again after 300ms
            setTimeout(() => {
              if (!scrollToQuestion()) {
                // Last attempt after 600ms
                setTimeout(scrollToQuestion, 600);
              }
            }, 300);
          }
        }, 100);
        
        // Remove animation class after animation completes
        setTimeout(() => setNewQuestionId(null), 3500);
      }
    } catch (err) {
      console.error('Question submission error:', err);
      const errorMsg = isPreviewReadOnlyError(err)
        ? PREVIEW_ONLY_MESSAGE
        : err.response?.data || err.message || 'Failed to submit question. Please try again.';
      setSubmitMessage(`${errorMsg}`);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };
  // Handle vote/unvote on question
  const handleVote = async (questionId) => {
    if (isPreview) {
      return;
    }
    const hasVoted = votedQuestionIds.has(questionId);

    try {
      if (hasVoted) {
        // Unvote
        await unvoteQuestionMutation.mutateAsync({ 
          questionId, 
          anonymousUserId 
        });
        
        // Optimistically update local state
        setVotedQuestionIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(questionId);
          return newSet;
        });
      } else {
        // Vote
        await voteQuestionMutation.mutateAsync({ 
          questionId, 
          anonymousUserId 
        });
        
        // Optimistically update local state
        setVotedQuestionIds(prev => new Set([...prev, questionId]));
      }
    } catch (err) {
      console.error(hasVoted ? 'Unvote error:' : 'Vote error:', err);
      const errorMsg = isPreviewReadOnlyError(err)
        ? PREVIEW_ONLY_MESSAGE
        : err.response?.data || err.message || (hasVoted ? 'Failed to remove vote. Please try again.' : 'Failed to vote. Please try again.');
      setSubmitMessage(`${errorMsg}`);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  // Helper function to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to extract user-friendly error message
  const getErrorMessage = (error) => {
    if (!error) return 'An unknown error occurred';

    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.response.statusText || '';

      if (status === 401) {
        return 'Unauthorized. Please log in again.';
      } else if (status === 403 || status === 503) {
        return 'Access denied. Please verify your permissions.';
      } else if (status === 404) {
        return 'Question not found.';
      } else if (status === 429) {
        return 'Too many requests. Please try again later.';
      } else if (status === 500) {
        return 'Server error. Please try again later.';
      } else if (typeof message === 'string' && message) {
        return message;
      }
      
      return `Failed to load Q&A (Error ${status})`;
    }

    if (error.request) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }

    if (typeof error.message === 'string' && error.message) {
      return error.message;
    }

    return 'An error occurred while loading Q&A. Please try again.';
  };

  // Skeleton loading component
  const SkeletonLoader = () => (
    <div className="w-full bg-gradient-to-b from-accent-20 to-accent-10 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="text-center mb-8">
          <div className="h-8 bg-primary-20 rounded-lg w-64 mx-auto mb-2 animate-pulse"></div>
          <div className="h-4 bg-primary-10 rounded w-48 mx-auto animate-pulse"></div>
        </div>
        <div className="bg-surface rounded-xl shadow-sm border border-accent-50 p-6 mb-6">
          <div className="space-y-4">
            <div className="h-4 bg-primary-10 rounded w-32 animate-pulse"></div>
            <div className="h-12 bg-primary-10 rounded animate-pulse"></div>
            <div className="h-24 bg-primary-10 rounded animate-pulse"></div>
            <div className="h-10 bg-primary-20 rounded w-32 animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (error) {
    const errorMessage = getErrorMessage(error);
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="text-center max-w-md">
          <svg className="mx-auto h-16 w-16 text-red-500 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
          </svg>
          <h2 className="text-xl font-semibold text-secondary mb-3">Error Loading Q&A</h2>
          <p className="text-secondary-70 mb-6 leading-relaxed">{errorMessage}</p>
          <Button
            onClick={() => refetch()}
            variant="primary"
            size="lg"
            icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gradient-to-b from-accent-20 to-accent-10 relative">
      <NameModal
        isOpen={isNameModalOpen}
        pendingName={pendingName}
        onChangePendingName={setPendingName}
        onCancel={() => {
          markNameModalSkipped();
          setIsNameModalOpen(false);
        }}
        onSave={() => {
          const trimmed = saveUserNameFromModal(pendingName);
          if (!trimmed) {
            return;
          }
          setName(trimmed);
          setPendingName('');
          setIsNameModalOpen(false);
        }}
      />
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mb-2">
            <h1 className="text-2xl font-semibold text-secondary">
              Q&A
            </h1>
          </div>
          <p className="text-secondary-70">
            Have a question for the couple? Ask away and vote on your favorites!
          </p>
        </div>

        {/* Question Form */}
        <div className="bg-surface rounded-xl shadow-sm border border-accent-50 p-4 sm:p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <span className="block text-sm font-medium text-secondary mb-1">
                Your Name
              </span>
              <div className="flex items-center gap-2 text-xs text-secondary-80">
                <div className="flex-1 min-w-0 truncate">
                  {name ? (
                    <span className="truncate block">{name}</span>
                  ) : (
                    <span className="italic text-secondary-60 truncate block">
                      You have not set your name
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="inline-flex flex-shrink-0 items-center gap-1 text-xs text-primary hover:text-primary-80 transition-colors"
                  onClick={() => {
                    setPendingName(name || '');
                    setIsNameModalOpen(true);
                  }}
                >
                  <PiPencilSimpleLine className="w-3 h-3" />
                  <span>Edit</span>
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="question-text" className="block text-sm font-medium text-secondary mb-2">
                Your Question
              </label>
              <textarea
                id="question-text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Ask your question here..."
                maxLength={2000}
                rows={4}
                className="w-full px-4 py-3 bg-accent-20 text-base sm:text-sm leading-relaxed border border-accent rounded-lg focus:ring-2 focus:ring-primary-50 focus:border-primary transition-colors placeholder-secondary-60 resize-none"
                required
              />
              <div className="text-xs text-secondary-60 mt-1 text-right">
                {question.length}/2000
              </div>
            </div>

            {submitMessage && (
              <div className={"p-3 rounded-lg text-sm 'bg-red-50 text-red-800 border border-red-200"}>
                {submitMessage}
              </div>
            )}

            {isPreview ? <PreviewNotice id="qanda-ask-preview-notice" /> : null}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={isPreview || createQuestionMutation.isPending || !name.trim() || !question.trim()}
              isLoading={createQuestionMutation.isPending}
              aria-describedby={isPreview ? 'qanda-ask-preview-notice' : undefined}
              icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            >
              Submit Question
            </Button>
          </form>
        </div>

        {/* Questions List */}
        <div className="bg-surface rounded-xl shadow-sm border border-accent-50 p-4 sm:p-6 no-scroll-anchor">
          <h2 className="text-lg font-medium text-secondary mb-4">
            Questions ({allQuestions.length})
          </h2>

          {isPreview && allQuestions.length > 0 ? (
            <PreviewNotice id="qanda-vote-preview-notice" className="mb-4" />
          ) : null}

          {allQuestions.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-16 w-16 text-primary-40 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-secondary-70">No questions yet. Be the first to ask!</p>
            </div>
          ) : (
            <>
              <div ref={animateRef} className="space-y-4 mb-6">
                {allQuestions.map((q) => {
                  const hasVoted = votedQuestionIds.has(q.id);
                  const isNewQuestion = newQuestionId === q.id;
                  
                  return (
                    <div
                      key={q.id}
                      ref={(el) => {
                        if (el) {
                          questionRefs.current[q.id] = el;
                        }
                      }}
                      className={`bg-accent-10 rounded-lg p-4 border border-accent-30 hover:shadow-md transition-all duration-300 ${
                        isNewQuestion ? 'successful-post-animation ring-2 ring-primary ring-opacity-50' : ''
                      }`}
                    >
                      {/* Top row: Name/Date + Vote button (mobile) / Name + Date (desktop) */}
                      <div className="flex flex-row items-start justify-between gap-3 mb-2">
                        <div className="min-w-0">
                          <h3 className="font-medium text-secondary truncate">
                            {q.name}
                          </h3>
                          {/* Date - mobile only, below name */}
                          {q.createdAt && (
                            <span className="sm:hidden block text-xs text-secondary-60">
                              {formatDate(q.createdAt)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {/* Date - desktop only in top row */}
                          {q.createdAt && (
                            <span className="hidden sm:inline text-xs text-secondary-60 whitespace-nowrap">
                              {formatDate(q.createdAt)}
                            </span>
                          )}
                          {/* Vote button - visible on mobile only in top row */}
                          <button
                            onClick={() => handleVote(q.id)}
                            disabled={isPreview || voteQuestionMutation.isPending || unvoteQuestionMutation.isPending}
                            aria-describedby={isPreview ? 'qanda-vote-preview-notice' : undefined}
                            className={`sm:hidden flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all duration-200 ${
                              hasVoted
                                ? 'bg-primary text-surface shadow-sm hover:bg-primary-80 cursor-pointer'
                                : 'bg-accent-20 text-secondary hover:bg-primary hover:text-surface border border-accent-30'
                            }`}
                            aria-label={hasVoted ? 'Remove your vote' : 'Vote for this question'}
                            title={hasVoted ? 'Click to remove your vote' : 'Vote for this question'}
                          >
                            {hasVoted ? (
                              <PiThumbsUpFill className="w-5 h-5" />
                            ) : (
                              <PiThumbsUp className="w-5 h-5" />
                            )}
                            <span className="font-semibold">{q.votes || 0}</span>
                          </button>
                        </div>
                      </div>
                      {/* Bottom row: Question + Vote button (desktop) */}
                      <div className="flex flex-row items-center gap-4">
                        <p className="flex-1 text-secondary-80 text-sm leading-relaxed whitespace-pre-wrap">
                          {q.question}
                        </p>
                        {/* Vote button - visible on desktop only, vertically centered */}
                        <button
                          onClick={() => handleVote(q.id)}
                          disabled={isPreview || voteQuestionMutation.isPending || unvoteQuestionMutation.isPending}
                          aria-describedby={isPreview ? 'qanda-vote-preview-notice' : undefined}
                          className={`hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all duration-200 flex-shrink-0 ${
                            hasVoted
                              ? 'bg-primary text-surface shadow-sm hover:bg-primary-80 cursor-pointer'
                              : 'bg-accent-20 text-secondary hover:bg-primary hover:text-surface border border-accent-30'
                          }`}
                          aria-label={hasVoted ? 'Remove your vote' : 'Vote for this question'}
                          title={hasVoted ? 'Click to remove your vote' : 'Vote for this question'}
                        >
                          {hasVoted ? (
                            <PiThumbsUpFill className="w-5 h-5" />
                          ) : (
                            <PiThumbsUp className="w-5 h-5" />
                          )}
                          <span className="font-semibold">{q.votes || 0}</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {hasNextPage && (
                <div className="text-center">
                  <Button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    variant="primary"
                    size="md"
                    isLoading={isFetchingNextPage}
                    icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>}
                  >
                    {isFetchingNextPage ? 'Loading...' : 'Load More'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QAndA;
